/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable no-shadow */
import { find, map } from 'lodash';
import { Box, Button, makeStyles, Typography } from '@material-ui/core';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';

import ClusterGroup from './ClusterGroup';
import SiteCluster from './SiteCluster';
import SiteMarker from './SiteMarker';
import StatusBlock from '../../status-block/StatusBlock';
import { groupSiteStatus, sortSites } from '../MapUtils';
import { statuses } from '../../../utilities/StatusUtilities';

const MARKER_SIZE = 24;
const CLUSTER_SIZE = 42;
const MAX_TOOLTIP_ITEMS = 15;

export default function SiteClusterGroup(props) {
    const { sites, activeSite, onSiteClick } = props;

    const activeItem = find(sites, (site) => site.site.uuid === activeSite?.uuid);

    const tooltipOptions = {
        interactive: true,
        permanent: true,
    };

    return (
        <ClusterGroup
            items={sites}
            activeItem={activeItem}
            clusterRadius={32}
            tooltipRenderer={(items) => <ClusteredTooltip items={items} onSiteClick={onSiteClick} />}
            tooltipOptions={tooltipOptions}
            getMarkerPosition={({ site }) => [site.latitude, site.longitude]}
            markerSize={MARKER_SIZE}
            renderMarker={({ item, isActive }) => (
                <SiteMarker size={MARKER_SIZE} status={item.status} isActive={isActive} />
            )}
            clusterSize={CLUSTER_SIZE}
            renderCluster={({ clusterItems, isActive }) => (
                <SiteCluster size={CLUSTER_SIZE} sites={clusterItems} isActive={isActive} />
            )}
            onItemClick={(item) => onSiteClick(item.site)}
        />
    );
}

const useClusteredTooltipStyles = makeStyles((theme) => ({
    root: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.alignItemsCenter,

        color: theme.palette.text.primary,

        '& > *': {
            paddingLeft: theme.spacing(1),
        },

        '& > *:first-child': {
            paddingLeft: 0,
        },
    },

    alignRight: {
        textAlign: 'right',
        fontWeight: 500,
        width: 30,
    },

    siteContainer: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionColumn,
        ...theme.mixins.flex.alignItemsStretch,
    },
}));

function ClusteredTooltip(props) {
    const { items, onSiteClick } = props;

    const classes = useClusteredTooltipStyles();

    if (items.length > MAX_TOOLTIP_ITEMS) {
        const groupedStatus = groupSiteStatus(items);
        const elements = [];

        map(groupedStatus, (count, statusColor) => {
            if (count > 0) {
                elements.push(
                    <div key={statusColor} className={classes.root}>
                        <StatusBlock size={12} color={statusColor} />
                        <Box width={60}>
                            <Typography variant="caption">{statuses[statusColor].name}:</Typography>
                        </Box>
                        <Typography variant="caption" className={classes.alignRight}>
                            {count}
                        </Typography>
                    </div>
                );
            }
        });

        return elements;
    }

    const sites = sortSites(items);
    return (
        <div className={classes.siteContainer}>
            {sites.map((item) => (
                <SiteTooltip
                    key={item.site.uuid}
                    site={item.site}
                    status={item.status}
                    onClick={() => onSiteClick(item.site)}
                />
            ))}
        </div>
    );
}

const useSiteTooltipStyles = makeStyles((theme) => ({
    root: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.justifyContentFlexStart,
    },

    statusBlock: {
        marginRight: theme.spacing(1),
    },
}));

function SiteTooltip(props) {
    const { site, status, onClick } = props;
    const classes = useSiteTooltipStyles();

    return (
        <Button
            className={classes.root}
            onClick={onClick}
            size="small"
            title={i18n._(t`Show details for site: ${site.name}`)}
        >
            <StatusBlock className={classes.statusBlock} size={12} color={status?.worst || 'gray'} />
            <Typography variant="caption" className={classes.siteText}>
                {site.name}
            </Typography>
        </Button>
    );
}
